/**
 * Typed wrappers for the `/salescoach/api/quality/*` endpoints (Plan 07 Task 7.1).
 *
 * Mirrors `engagement-api.ts` conventions:
 *   1. `buildQuery(filters)` strips defaults + resolves preset→from/to.
 *   2. Granularity / limit / sort / includePrev are appended as extra params.
 *   3. Return type is always the raw `ApiEnvelope<T>` — unwrapping is the
 *      hook's job (`use-quality.ts`).
 *
 * Four endpoints per Plan 03-03 backend (bug F3 — the safety-refusals
 * wrapper was removed; backend endpoint still exists):
 *   GET /kpis                   → QualityKpis
 *   GET /rating-trend           → RatingTrendPoint[]
 *   GET /rating-histogram       → RatingHistogram (current + optional previous)
 *   GET /by-user                → QualityByUserRow[] (limit + sort + minRatings)
 */

import { apiFetch } from '@/services/api';
import { buildApiUrl } from '@/services/api-client';
import type { Filters } from '@/lib/filters-schema';
import type {
  ApiEnvelope,
  QualityByUserRow,
  QualityByUserSortColumn,
  QualityKpis,
  RatingHistogram,
  RatingTrendPoint,
} from './api-types';

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

export async function getQualityKpis(
  filters: Filters,
): Promise<ApiEnvelope<QualityKpis>> {
  return apiFetch<ApiEnvelope<QualityKpis>>(
    buildApiUrl('/salescoach/api/quality/kpis', filters),
  );
}

export async function getRatingTrend(
  filters: Filters,
  granularity: Filters['granularity'],
): Promise<ApiEnvelope<RatingTrendPoint[]>> {
  return apiFetch<ApiEnvelope<RatingTrendPoint[]>>(
    buildApiUrl('/salescoach/api/quality/rating-trend', filters, { granularity }),
  );
}

/**
 * Fetch the rating histogram with optional prev-period overlay.
 *
 * `includePrev=true` adds `?includePrev=true` — backend parses boolean-ish
 * flags (`true/false/1/0`); we emit the canonical string form so the server
 * regex match is obvious in logs.
 */
export async function getRatingHistogram(
  filters: Filters,
  options?: { includePrev?: boolean },
): Promise<ApiEnvelope<RatingHistogram>> {
  return apiFetch<ApiEnvelope<RatingHistogram>>(
    buildApiUrl('/salescoach/api/quality/rating-histogram', filters, {
      includePrev: options?.includePrev ? true : undefined,
    }),
  );
}

// bug F3 — `getSafetyRefusalsTrend` was removed with the Safety refusals
// chart on QualityPage. Backend `/salescoach/api/quality/safety-refusals-trend`
// is still live for future tooling or an out-of-tree consumer.

/** /by-user params — short `<col>.<dir>` sort string matches the server regex. */
export interface QualityByUserParams {
  limit?: number;
  sort?: `${QualityByUserSortColumn}.${'asc' | 'desc'}`;
  /** Minimum rated-requests threshold for a user to appear in the table. */
  minRatings?: number;
}

export async function getQualityByUser(
  filters: Filters,
  params: QualityByUserParams = {},
): Promise<ApiEnvelope<QualityByUserRow[]>> {
  return apiFetch<ApiEnvelope<QualityByUserRow[]>>(
    buildApiUrl('/salescoach/api/quality/by-user', filters, {
      limit: params.limit,
      sort: params.sort,
      minRatings: params.minRatings,
    }),
  );
}
